import { ByecodeUIThemeProvider, IconFont } from '@byecode/ui'
import { MessageContainer } from '@byecode/ui/components/Message'
import { createTheme } from '@byecode/ui/theme/stitches.config'
import { AnimationProvider } from '@lighthouse/block'
import type { NavEvents, NavigationBaseItem, PageStackTree, VariableADTvalue } from '@lighthouse/core'
import { ApplicationSettingJumpType, NavHandleEvent, PAGE_STACK_DISPLAY_TYPE } from '@lighthouse/core'
import type { PageOverlayState } from '@lighthouse/shared'
import {
    ApplicationContainer,
    AppThemeContextProvider,
    combineBackgroundStyle,
    commonPages,
    fieldConvertValue,
    getBackgroundStyle,
    getCursorStyle,
    getJumpUrl,
    getVeinsStyle,
    LanguageProvider,
    PAGE_CONTAINER_HOST,
    PAGE_CONTAINER_HOST_ID,
    PAGE_LAYOUT_HOST,
    PAGE_MODAL,
    PageNoAccess,
    PageOverlay,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { find, findLast } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { setAppRoleAtom } from '@/atoms/application/action'
import { developerRole } from '@/atoms/application/constants'
import {
    applicationSettingAtom,
    applicationThemeColorAtom,
    blockAnimationStateAtom,
    isMobileDeviceAtom,
    languageAtom
} from '@/atoms/application/state'
import { closeAllPageLayerAtom, closeCurrentPageLayerStackAtom, openPageStackAtom } from '@/atoms/page/action'
import { createNodeLoadingAtom, homePageAtom, pageInternalStateAtomFamily, pageListAtom, pageStackLayersAtom } from '@/atoms/page/state'
import { AsideType } from '@/atoms/page/types'
import { stackFactory } from '@/atoms/page/utils'
import DragLoading from '@/components/DragLoading'
import { NestedPageWrapper } from '@/components/NestedPageWrapper'
import { PageTool } from '@/components/PageTool'
import { RootPageProvider } from '@/contexts/PageContext'
import { useAppLanguageList, useApplication, useCurrentBreakPoint } from '@/hooks/useApplication'
import { usePageList } from '@/hooks/usePage'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useToggleLang } from '@/hooks/useToggleLang'
import { useUserPageList } from '@/hooks/useUserPageList'

import { PageContainer } from './PageContainer'
import { PageFooter } from './PageFooter'
import { SettingAside } from './SettingAside'

const BackButton = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 6px;
`

const SCxPageBody = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
    justify-content: stretch;
    height: 100%;
    overflow: hidden;
`

const SCxPageContent = styled.div`
    position: relative;
    padding: 48px 12px 52px 12px;
    width: 100%;
    max-height: 100%;
    overflow: hidden;
    background-color: var(--color-gray-50);
`

const SCxPageWrapper = styled.div<{ isMobile?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 12px;
    box-shadow: none;

    ${({ isMobile }) =>
        isMobile
            ? css`
                  padding: 0 8px;
                  overflow: auto;
              `
            : css`
                  box-shadow: 0px 4px 12px 0px #1018281a;
                  outline: 1px solid var(--color-gray-200);
              `}
`

const SCxContainer = styled.div`
    /* position: relative; */
    /* z-index: 1; */
    flex: 1;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

interface PageLayoutProps {
    pageId: string
    stackId: string
    loading?: boolean
    onFreshPage?: () => void
}

export const PageLayout: React.FC<PageLayoutProps> = props => {
    const { pageId, stackId, onFreshPage } = props

    const isDragLoading = useAtomData(createNodeLoadingAtom)
    const app = useApplication()
    const {
        id: appId,
        config: { theme, webTag }
    } = app
    const breakPoint = useCurrentBreakPoint()
    const blockAnimationState = useAtomData(blockAnimationStateAtom)
    const { run: setBlockAnimationStateAtom } = useAtomAction(blockAnimationStateAtom)
    const animationContextProps = useMemo(
        () => ({
            queue: blockAnimationState,
            onAnimationStart: (params: { blockId: string; scope?: string }) => {
                const { blockId, scope } = params
                setBlockAnimationStateAtom(draft => {
                    draft.forEach((v, i) => {
                        if (v.blockId === blockId && v.scope === scope) {
                            // draft[i].state = AnimationState.Running
                        }
                        // draft[i].played = false
                        // draft[i].state = AnimationState.Running
                    })
                })
            },
            onAnimationComplete: (params: { blockId: string; scope?: string }) => {
                const { blockId, scope } = params
                setBlockAnimationStateAtom(draft => {
                    const animate = findLast(v => v.blockId === blockId && v.scope === scope, draft)
                    if (animate) {
                        // animate.state = AnimationState.Completed
                        animate.played = false
                    }
                })
            }
        }),
        [blockAnimationState, setBlockAnimationStateAtom]
    )

    const { run: setAppRole } = useAtomAction(setAppRoleAtom)

    const language = useAtomData(languageAtom)
    const isMobile = useAtomData(isMobileDeviceAtom)

    const { visitPageList, allRolePageList, user, developerRoleUserId } = useUserPageList()
    const allPageList = usePageList()
    const homePage = useAtomData(homePageAtom)
    const visitPage = useMemo(() => find(item => item.id === pageId, visitPageList), [pageId, visitPageList])
    const isVisiblePage = Boolean(visitPage)

    const pageStackLayers = useAtomData(pageStackLayersAtom)
    const lastLayer = useMemo(() => pageStackLayers[pageStackLayers.length - 1] as PageStackTree | undefined, [pageStackLayers])
    const innerPageName = useAtomData(
        pageListAtom,
        useCallback(s => lastLayer && s.find(item => item.id === lastLayer.pageId)?.name, [lastLayer])
    )

    // const isFormExistValue = useAtomData(
    //     pageStackOfFormContainerBlockChangedAtom,
    //     useCallback(s => s[lastLayer?.stackId ?? ''], [lastLayer?.stackId])
    // )

    const { run: closeCurrentPageLayerStack } = useAtomAction(closeCurrentPageLayerStackAtom)
    const { run: closeAllPageLayers } = useAtomAction(closeAllPageLayerAtom)

    // 2025-08-05 灿白说去掉关闭弹窗后的校验表单未提交
    const handleModalClose = useCallback(() => {
        closeAllPageLayers()
        // if (!isFormExistValue) {
        //     closeAllPageLayers()
        //     return
        // }
        // Modal.confirm({
        //     title: '表单中的数据未提交',
        //     content: `关闭页面后数据不会保存`,
        //     okText: '确认关闭',
        //     target: PAGE_CONTAINER_HOST
        // })
        //     .then(isConfirm => {
        //         isConfirm && closeAllPageLayers()
        //     })
        //     // eslint-disable-next-line no-console
        //     .catch(console.warn)
    }, [closeAllPageLayers])

    const handleModalBack = useCallback(() => {
        closeCurrentPageLayerStack()
        // if (!isFormExistValue) {
        //     closeCurrentPageLayerStack()
        //     return
        // }
        // Modal.confirm({
        //     title: '表单中的数据未提交',
        //     content: `关闭页面后数据不会保存`,
        //     okText: '确认关闭',
        //     target: PAGE_CONTAINER_HOST
        // })
        //     .then(isConfirm => {
        //         isConfirm && closeCurrentPageLayerStack()
        //     })
        //     // eslint-disable-next-line no-console
        //     .catch(console.warn)
    }, [closeCurrentPageLayerStack])

    const layer = useMemo(() => {
        if (!lastLayer) {
            return
        }

        const { pageId, stackId, pageStackDisplayType } = lastLayer
        if (!stackId) {
            return
        }

        switch (pageStackDisplayType) {
            case PAGE_STACK_DISPLAY_TYPE.modal:
            case PAGE_STACK_DISPLAY_TYPE.drawer: {
                return {
                    type: pageStackDisplayType,
                    backgroundColor: webTag.backgroundColor,
                    props: {
                        target: PAGE_CONTAINER_HOST,
                        title: innerPageName,
                        classNames: {
                            modal: PAGE_MODAL
                        },
                        onClose: handleModalClose,
                        leftSlot: pageStackLayers.length > 1 && (
                            <BackButton onClick={handleModalBack}>
                                <IconFont type="ArrowLeftSmall" color="var(--color-gray-400)" size={16} />
                            </BackButton>
                        ),
                        children: <NestedPageWrapper pageId={pageId} stackId={stackId} />
                    }
                }
            }

            default: {
                break
            }
        }
    }, [lastLayer, innerPageName, webTag.backgroundColor, handleModalClose, pageStackLayers.length, handleModalBack])

    const { run: openPageStack } = useAtomAction(openPageStackAtom)

    const handleGoHome = useCallback(() => {
        if (!homePage) {
            return
        }
        openPageStack(stackFactory({ pageId: homePage.id }))
    }, [homePage, openPageStack])

    const asideType = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.asideType, [])
    )

    const { run: setPageInternalState } = useAtomAction(pageInternalStateAtomFamily(stackId))
    const onSelectNavBar = useCallback(() => {
        setPageInternalState(draft => {
            draft.asideType = AsideType.NAVBAR
            draft.selectedNodes = undefined
        })
    }, [setPageInternalState])

    // 导航栏点击回调
    const handleCommonCallBack = useCallback(
        (buttonEvents: NavEvents) => {
            const { params, handleEvent } = buttonEvents
            const isHaveNoneParam = !params.some(Boolean)
            if (isHaveNoneParam) {
                return
            }
            switch (handleEvent) {
                case NavHandleEvent.OPEN_PAGE: {
                    const pageId = params[0].replace('/page/', '')
                    const isExitPage = allPageList.find(page => page.id === pageId)
                    if (commonPages.includes(pageId)) {
                        break
                    }
                    if (!isExitPage) {
                        break
                    }
                    openPageStack(
                        stackFactory({
                            pageId
                        })
                    )
                    break
                }
                case NavHandleEvent.OPEN_LINK: {
                    const linkUrl = params[0]
                    window.open(getJumpUrl(linkUrl))
                    break
                }
                default: {
                    break
                }
            }
        },
        [allPageList, openPageStack]
    )

    const handleLogout = useCallback(() => {
        setAppRole(developerRole.userId)
    }, [setAppRole])

    const handleToLink = useCallback(
        (navigation?: NavigationBaseItem) => {
            if (!navigation) {
                return
            }
            const { value: linkUrl, jumpType } = navigation
            if (!linkUrl) {
                return
            }
            switch (jumpType) {
                case ApplicationSettingJumpType.linkUrl: {
                    return window.open(getJumpUrl(linkUrl))
                }
                case ApplicationSettingJumpType.page: {
                    if (commonPages.includes(linkUrl)) {
                        return
                    }
                    const pageId = linkUrl.replace('/page/', '')
                    openPageStack(stackFactory({ pageId }))
                    break
                }
                default: {
                    break
                }
            }
        },
        [openPageStack]
    )
    // 切换语言
    const { handleToggleLang } = useToggleLang()

    const parseVariable = useParseVariableWithSubscription(visitPage?.breakPoint?.design?.background?.image?.resource)
    const parseBackgroundVariableImage = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariable]
    )
    const parseVariableColor = useParseVariableWithSubscription(visitPage?.breakPoint?.design?.background?.variable)
    const parseBackgroundVariableColor = useCallback(
        (value: VariableADTvalue | undefined) => {
            const field = parseVariableColor(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseVariableColor]
    )

    const palettes = useAtomData(
        applicationSettingAtom,
        useCallback(s => {
            return s?.theme.palettes ?? []
        }, [])
    )
    const backgroundStyle = useMemo(() => {
        const styles = {
            ...combineBackgroundStyle([
                getBackgroundStyle({
                    appId,
                    value: visitPage?.breakPoint?.design?.background,
                    palettes,
                    parseVariableImage: parseBackgroundVariableImage,
                    parseVariableColor: parseBackgroundVariableColor
                }),
                getVeinsStyle(visitPage?.breakPoint?.design?.veins, palettes)
            ])
        }

        return {
            ...styles,
            backgroundColor: styles.backgroundColor || webTag.backgroundColor
        }
    }, [
        webTag.backgroundColor,
        appId,
        visitPage?.breakPoint?.design?.background,
        visitPage?.breakPoint?.design?.veins,
        palettes,
        parseBackgroundVariableImage,
        parseBackgroundVariableColor
    ])

    const appThemeColor = useAtomData(applicationThemeColorAtom)
    const customBuiTheme = createTheme({ colors: { colorPrimary: appThemeColor, colorPrimaryHover: appThemeColor } })

    const languageList = useAppLanguageList()
    const languageValue = useMemo(
        () => ({
            language,
            languageList
        }),
        [language, languageList]
    )

    const rootPageProvideValue = useMemo(() => ({ refreshRootPage: onFreshPage }), [onFreshPage])
    return (
        <RootPageProvider value={rootPageProvideValue}>
            <AppThemeContextProvider theme={theme}>
                {/* <SCxPageContainer> */}
                <SCxPageBody>
                    <SCxPageContent className={customBuiTheme}>
                        <PageTool />
                        <ByecodeUIThemeProvider colorPrimary={appThemeColor} colorPrimaryHover={appThemeColor}>
                            <SCxPageWrapper isMobile={isMobile}>
                                <LanguageProvider value={languageValue}>
                                    <AnimationProvider value={animationContextProps}>
                                        <ApplicationContainer
                                            id={PAGE_CONTAINER_HOST_ID}
                                            application={app}
                                            pageList={allRolePageList}
                                            selected={AsideType.NAVBAR === asideType}
                                            previewMode={isMobile ? 'simulationMobile' : 'simulationWebsite'}
                                            breakPoint={breakPoint}
                                            activePage={pageId}
                                            language={language}
                                            userData={user}
                                            styles={{
                                                container: isMobile
                                                    ? {
                                                          width: 'fit-content',
                                                          margin: '0 auto',
                                                          minHeight: 600
                                                      }
                                                    : undefined,
                                                wrapper: { ...backgroundStyle, ...getCursorStyle(appId, visitPage?.cursor) },
                                                mobileHeader: {
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            key={developerRoleUserId}
                                            onCommonCallBack={handleCommonCallBack}
                                            onSelect={onSelectNavBar}
                                            onLogout={handleLogout}
                                            onToLink={handleToLink}
                                            onChangeLanguage={handleToggleLang}
                                        >
                                            <SCxContainer id={PAGE_LAYOUT_HOST}>
                                                <MessageContainer isMobile={isMobile} containerId={PAGE_CONTAINER_HOST_ID} />
                                                {isVisiblePage && developerRoleUserId ? <PageContainer {...props} /> : <PageNoAccess onGoHome={handleGoHome} />}
                                                <PageOverlay layerProps={layer} />
                                                {isDragLoading && <DragLoading />}
                                            </SCxContainer>
                                        </ApplicationContainer>
                                    </AnimationProvider>
                                </LanguageProvider>
                            </SCxPageWrapper>
                        </ByecodeUIThemeProvider>
                        {/* 数据抽屉 */}
                        <PageFooter />
                    </SCxPageContent>

                    {/* 右侧设置栏 */}
                    <SettingAside />
                </SCxPageBody>
            </AppThemeContextProvider>
        </RootPageProvider>
    )
}
